import React, { useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { CheckCircleIcon, XCircleIcon, LinkIcon } from "@heroicons/react/24/outline";
import apiFunctions from "../../services/api-service";

/* Optional deep-link picker used in every notification-send dialog. Lets the
   admin choose what tapping the notification opens — mirrored into the
   backend's link_type/link_value fields (see model/notificationschedule.js),
   which the send code turns into the FCM `data` block. "Preview" resolves the
   id against the real record so a typo is caught before sending, not after. */
type LinkType = "none" | "post" | "profile" | "chat";

type Props = {
    linkType: LinkType;
    linkValue: string;
    onLinkTypeChange: (type: LinkType) => void;
    onLinkValueChange: (value: string) => void;
};

const LINK_TYPE_OPTIONS: { label: string; value: LinkType }[] = [
    { label: "None", value: "none" },
    { label: "A Post", value: "post" },
    { label: "A User's Profile", value: "profile" },
    { label: "A Chat With a User", value: "chat" },
];

const NotificationLinkPicker: React.FC<Props> = ({ linkType, linkValue, onLinkTypeChange, onLinkValueChange }) => {
    const [checking, setChecking] = useState(false);
    const [result, setResult] = useState<{ ok: boolean; label: string } | null>(null);

    const placeholder =
        linkType === "post" ? "Paste the Post ID" :
            linkType === "profile" ? "Paste the User ID" :
                linkType === "chat" ? "Paste the User ID to chat with" : "";

    const checkTarget = async () => {
        if (!linkValue.trim()) return;
        setChecking(true);
        setResult(null);
        try {
            if (linkType === "post") {
                const res = await apiFunctions.get(`/postmobile/post/${linkValue.trim()}`);
                if (res?.data?.success && res.data.data) {
                    const { userName, postText } = res.data.data;
                    setResult({ ok: true, label: `${userName}: ${(postText || "").slice(0, 60) || "(no text)"}` });
                } else {
                    setResult({ ok: false, label: "Post not found" });
                }
            } else if (linkType === "profile" || linkType === "chat") {
                const res = await apiFunctions.get(`/usermobile/mobile/getbyid/${linkValue.trim()}`);
                if (res?.data?.success && res.data.data) {
                    setResult({ ok: true, label: res.data.data.fullname || res.data.data.name || "User found" });
                } else {
                    setResult({ ok: false, label: "User not found" });
                }
            }
        } catch {
            setResult({ ok: false, label: "Not found" });
        } finally {
            setChecking(false);
        }
    };

    return (
        <div className="flex flex-col gap-2 border rounded p-3" style={{ borderColor: "#264164" }}>
            <label className="text-sm font-medium flex items-center gap-2" style={{ color: "#264164" }}>
                <LinkIcon className="w-4 h-4" />
                Link (optional) — what tapping the notification opens
            </label>

            <Dropdown
                value={linkType}
                options={LINK_TYPE_OPTIONS}
                onChange={(e) => {
                    onLinkTypeChange(e.value);
                    onLinkValueChange("");
                    setResult(null);
                }}
                optionLabel="label"
                optionValue="value"
                className="w-full"
            />

            {linkType !== "none" && (
                <>
                    <div className="flex gap-2">
                        <InputText
                            value={linkValue}
                            onChange={(e) => {
                                onLinkValueChange(e.target.value);
                                setResult(null);
                            }}
                            placeholder={placeholder}
                            className="w-full"
                        />
                        <Button
                            label="Check"
                            outlined
                            loading={checking}
                            disabled={!linkValue.trim()}
                            onClick={checkTarget}
                        />
                    </div>

                    {result && (
                        <span
                            className="text-xs flex items-center gap-1"
                            style={{ color: result.ok ? "#16a34a" : "#dc2626" }}
                        >
                            {result.ok ? <CheckCircleIcon className="w-4 h-4" /> : <XCircleIcon className="w-4 h-4" />}
                            {result.label}
                        </span>
                    )}
                </>
            )}
        </div>
    );
};

export default NotificationLinkPicker;
